export const PRICE_MATCH_POLICY_SCHEMA_VERSION = "2026-08-09";

export const POLICY_TARGET_MODE_PRODUCT = "PRODUCT" as const;
export const POLICY_TARGET_MODE_VARIANT = "VARIANT" as const;
export const POLICY_TARGET_MODES = [
  POLICY_TARGET_MODE_PRODUCT,
  POLICY_TARGET_MODE_VARIANT,
] as const;
export type PolicyTargetMode = (typeof POLICY_TARGET_MODES)[number];

export interface PolicyModeVariant {
  options: readonly { name: string; value: string }[];
}

export function resolvePolicyTargetMode(variants: readonly PolicyModeVariant[]): PolicyTargetMode {
  if (variants.length > 1) return POLICY_TARGET_MODE_VARIANT;
  if (variants.length === 1 && variants[0]?.options.some(isSelectablePolicyOption)) {
    return POLICY_TARGET_MODE_VARIANT;
  }
  return POLICY_TARGET_MODE_PRODUCT;
}

function isSelectablePolicyOption(option: { name: string; value: string }): boolean {
  const name = option.name.trim().toLowerCase();
  const value = option.value.trim().toLowerCase();
  return value.length > 0 && !(name === "title" && value === "default title");
}

export const POLICY_CONDITIONS = ["NEW"] as const;
export type PolicyCondition = (typeof POLICY_CONDITIONS)[number];

export const POLICY_STOCK_REQUIREMENTS = ["IN_STOCK", "ANY"] as const;
export type PolicyStockRequirement = (typeof POLICY_STOCK_REQUIREMENTS)[number];

export const POLICY_MISSING_COST_BEHAVIORS = ["MANUAL_REVIEW", "DISCOUNT_CAP_ONLY"] as const;
export type PolicyMissingCostBehavior = (typeof POLICY_MISSING_COST_BEHAVIORS)[number];

export const POLICY_UNKNOWN_COMPETITOR_BEHAVIOR_ALLOW_AUTOMATIC_CHECKS =
  "ALLOW_AUTOMATIC_CHECKS" as const;
export const POLICY_UNKNOWN_COMPETITOR_BEHAVIOR_MANUAL_REVIEW = "MANUAL_REVIEW" as const;
export const POLICY_UNKNOWN_COMPETITOR_BEHAVIOR_BLOCK_REQUEST = "BLOCK_REQUEST" as const;
export const POLICY_UNKNOWN_COMPETITOR_BEHAVIORS = [
  POLICY_UNKNOWN_COMPETITOR_BEHAVIOR_ALLOW_AUTOMATIC_CHECKS,
  POLICY_UNKNOWN_COMPETITOR_BEHAVIOR_MANUAL_REVIEW,
  POLICY_UNKNOWN_COMPETITOR_BEHAVIOR_BLOCK_REQUEST,
] as const;
export type PolicyUnknownCompetitorBehavior = (typeof POLICY_UNKNOWN_COMPETITOR_BEHAVIORS)[number];

export const PRICE_MATCH_POLICY_OUTCOMES = [
  "AUTO_MATCH",
  "COUNTEROFFER_ALLOWED",
  "ALREADY_CHEAPER",
  "MANUAL_REVIEW",
  "REJECT",
] as const;
export type PriceMatchPolicyOutcome = (typeof PRICE_MATCH_POLICY_OUTCOMES)[number];

export const VARIANT_POLICY_OVERRIDE_ENABLED = "ENABLED" as const;
export const VARIANT_POLICY_OVERRIDE_DISABLED = "DISABLED" as const;
export const VARIANT_POLICY_OVERRIDE_STATUSES = [
  VARIANT_POLICY_OVERRIDE_ENABLED,
  VARIANT_POLICY_OVERRIDE_DISABLED,
] as const;
export type VariantPolicyOverrideStatus = (typeof VARIANT_POLICY_OVERRIDE_STATUSES)[number];

export type PriceMatchPolicyReasonCode =
  | "COMPETITOR_DOMAIN_NOT_APPROVED"
  | "SELLER_NOT_APPROVED"
  | "CONDITION_NOT_ALLOWED"
  | "COMPETITOR_OUT_OF_STOCK"
  | "STALE_COMPETITOR_PRICE"
  | "NON_PUBLIC_PRICE"
  | "REQUEST_LIMIT_EXCEEDED"
  | "CURRENCY_MISMATCH"
  | "MISSING_UNIT_COST"
  | "DISCOUNT_CAP_EXCEEDED"
  | "MARGIN_FLOOR_VIOLATED"
  | "ALREADY_CHEAPER"
  | "AUTO_MATCH_ELIGIBLE";

export interface PriceMatchPolicyScopeOverride {
  scope: "PRODUCT" | "COLLECTION" | "VENDOR" | "TAG";
  scopeId: string;
  enabled: boolean;
  policyOverride: Partial<Omit<PriceMatchPolicy, "scopeOverrides">>;
}

export interface PriceMatchPolicy {
  schemaVersion?: string;
  minimumMarginBps?: number;
  minimumOfferPriceMinor?: number;
  maxDiscountBps: number;
  competitorMustBeInStock: boolean;
  sameVariantRequired: boolean;
  samePackRequired: boolean;
  allowedCondition: PolicyCondition;
  offerTtlMinutes: number;
  allowCounteroffer: boolean;
  allowManualReview: boolean;
  maximumQuantity: number;
  approvedDomainIds?: string[];
  approvedSellerIds?: string[];
  stockRequirement?: PolicyStockRequirement;
  maximumPriceAgeMinutes?: number;
  requirePublicPrice?: boolean;
  requestLimitPerCustomerPerDay?: number;
  requestLimitPerProductPerDay?: number;
  automaticApprovalMaxDiscountBps?: number;
  manualReviewAboveDiscountBps?: number;
  counterofferMaxDiscountBps?: number;
  offerExpiryMinutes?: number;
  missingCostBehavior?: PolicyMissingCostBehavior;
  unknownCompetitorBehavior?: PolicyUnknownCompetitorBehavior;
  redemptionLimit?: number;
  allowStacking?: boolean;
  scopeOverrides?: PriceMatchPolicyScopeOverride[];
}

export interface VariantPolicyOverride {
  status: VariantPolicyOverrideStatus;
  rules: Partial<PriceMatchPolicy>;
}

export interface PriceMatchPolicySource {
  scope: "STORE" | "PRODUCT" | "VARIANT";
  sourceId: string;
  enabled: boolean;
}

export interface EffectivePriceMatchPolicy {
  policy: PriceMatchPolicy;
  sourceChain: PriceMatchPolicySource[];
}

export interface EffectivePriceMatchPolicyInput {
  store: PriceMatchPolicySource & { policy: PriceMatchPolicy };
  product?: PriceMatchPolicySource & { policyOverride: Partial<PriceMatchPolicy> };
  variant?: PriceMatchPolicySource & { policyOverride: Partial<PriceMatchPolicy> };
}

export interface PriceMatchPolicyEvaluationInput {
  policy: PriceMatchPolicy;
  currentPriceMinor: number;
  competitorPriceMinor: number;
  currency: string;
  competitorCurrency: string;
  competitorDomainApproved: boolean;
  sellerApproved: boolean;
  competitorCondition: PolicyCondition | "USED" | "REFURBISHED";
  competitorInStock: boolean;
  observedAt: string;
  now: string;
  isPublicPrice: boolean;
  requestCountForCustomerToday?: number;
  requestCountForProductToday?: number;
  unitCostMinor?: number;
}

export interface PriceMatchPolicyDecision {
  outcome: PriceMatchPolicyOutcome;
  publicReasonCodes: PriceMatchPolicyReasonCode[];
  internalReasonCodes: PriceMatchPolicyReasonCode[];
  missingData: string[];
  offerPriceMinor?: number;
  counterofferPriceMinor?: number;
  policySchemaVersion: string;
}

export const DEFAULT_PRICE_MATCH_POLICY: PriceMatchPolicy = {
  schemaVersion: PRICE_MATCH_POLICY_SCHEMA_VERSION,
  maxDiscountBps: 2_500,
  competitorMustBeInStock: true,
  sameVariantRequired: true,
  samePackRequired: true,
  allowedCondition: "NEW",
  offerTtlMinutes: 30,
  allowCounteroffer: false,
  allowManualReview: false,
  maximumQuantity: 10,
  stockRequirement: "IN_STOCK",
  maximumPriceAgeMinutes: 60,
  requirePublicPrice: true,
  requestLimitPerCustomerPerDay: 3,
  requestLimitPerProductPerDay: 25,
  automaticApprovalMaxDiscountBps: 1_500,
  manualReviewAboveDiscountBps: 2_500,
  offerExpiryMinutes: 30,
  missingCostBehavior: "MANUAL_REVIEW",
  unknownCompetitorBehavior: "MANUAL_REVIEW",
  redemptionLimit: 1,
  allowStacking: false,
};

export function resolveEffectivePriceMatchPolicy(
  input: EffectivePriceMatchPolicyInput,
): EffectivePriceMatchPolicy {
  const productPolicy = applyEnabledOverride(input.store.policy, input.product);
  const policy = applyEnabledOverride(productPolicy, input.variant);
  return {
    policy: withPolicySchemaVersion(policy),
    sourceChain: [input.store, input.product, input.variant].flatMap(toPolicySource),
  };
}

export function validatePriceMatchPolicy(policy: Partial<PriceMatchPolicy>): string[] {
  const errors = [
    validateBps("minimumMarginBps", policy.minimumMarginBps),
    validateMoneyFloor("minimumOfferPriceMinor", policy.minimumOfferPriceMinor),
    validateBps("maxDiscountBps", policy.maxDiscountBps),
    validatePositiveInteger("offerTtlMinutes", policy.offerTtlMinutes),
    validatePositiveInteger("maximumQuantity", policy.maximumQuantity),
    validatePositiveInteger("maximumPriceAgeMinutes", policy.maximumPriceAgeMinutes),
    validatePositiveInteger("requestLimitPerCustomerPerDay", policy.requestLimitPerCustomerPerDay),
    validatePositiveInteger("requestLimitPerProductPerDay", policy.requestLimitPerProductPerDay),
    validateBps("automaticApprovalMaxDiscountBps", policy.automaticApprovalMaxDiscountBps),
    validateBps("manualReviewAboveDiscountBps", policy.manualReviewAboveDiscountBps),
    validateBps("counterofferMaxDiscountBps", policy.counterofferMaxDiscountBps),
    validatePositiveInteger("offerExpiryMinutes", policy.offerExpiryMinutes),
    validatePositiveInteger("redemptionLimit", policy.redemptionLimit),
  ].filter((error): error is string => error !== undefined);

  return [...errors, ...validateThresholdOrder(policy)];
}

export function evaluatePriceMatchPolicy(
  input: PriceMatchPolicyEvaluationInput,
): PriceMatchPolicyDecision {
  const normalized = normalizeEvaluationInput(input);
  const rejection = evaluateHardRejections(normalized);
  if (rejection !== undefined) {
    return rejection;
  }

  const cheaper = evaluateAlreadyCheaper(normalized);
  if (cheaper !== undefined) {
    return cheaper;
  }

  const constrainedOffer = evaluateOfferConstraints(normalized);
  if (constrainedOffer !== undefined) {
    return constrainedOffer;
  }

  return approveAutomaticMatch(normalized);
}

export function toPublicPolicyDecision(
  decision: PriceMatchPolicyDecision,
): Omit<PriceMatchPolicyDecision, "internalReasonCodes"> {
  return {
    outcome: decision.outcome,
    publicReasonCodes: decision.publicReasonCodes,
    missingData: decision.missingData,
    ...(decision.offerPriceMinor === undefined
      ? {}
      : { offerPriceMinor: decision.offerPriceMinor }),
    ...(decision.counterofferPriceMinor === undefined
      ? {}
      : { counterofferPriceMinor: decision.counterofferPriceMinor }),
    policySchemaVersion: decision.policySchemaVersion,
  };
}

export function safePriceMatchPolicySummary(policy: PriceMatchPolicy): Record<string, unknown> {
  return {
    schemaVersion: policy.schemaVersion ?? PRICE_MATCH_POLICY_SCHEMA_VERSION,
    maxDiscountBps: policy.maxDiscountBps,
    minimumMarginBps: policy.minimumMarginBps ?? null,
    minimumOfferPriceMinor: policy.minimumOfferPriceMinor ?? null,
    competitorMustBeInStock: policy.competitorMustBeInStock,
    sameVariantRequired: policy.sameVariantRequired,
    samePackRequired: policy.samePackRequired,
    allowedCondition: policy.allowedCondition,
    offerTtlMinutes: policy.offerTtlMinutes,
    allowCounteroffer: policy.allowCounteroffer,
    allowManualReview: policy.allowManualReview,
    maximumQuantity: policy.maximumQuantity,
    requirePublicPrice: policy.requirePublicPrice ?? true,
    maximumPriceAgeMinutes: policy.maximumPriceAgeMinutes ?? null,
    requestLimitPerCustomerPerDay: policy.requestLimitPerCustomerPerDay ?? null,
    requestLimitPerProductPerDay: policy.requestLimitPerProductPerDay ?? null,
    missingCostBehavior: policy.missingCostBehavior ?? "MANUAL_REVIEW",
    unknownCompetitorBehavior: policy.unknownCompetitorBehavior ?? "MANUAL_REVIEW",
    redemptionLimit: policy.redemptionLimit ?? null,
    allowStacking: policy.allowStacking ?? false,
    scopeOverrideCount: policy.scopeOverrides?.length ?? 0,
  };
}

function applyEnabledOverride(
  base: PriceMatchPolicy,
  override: (PriceMatchPolicySource & { policyOverride: Partial<PriceMatchPolicy> }) | undefined,
): PriceMatchPolicy {
  if (override === undefined || !override.enabled) {
    return base;
  }

  return { ...base, ...override.policyOverride };
}

function toPolicySource(
  source:
    | (PriceMatchPolicySource & { policy: PriceMatchPolicy })
    | (PriceMatchPolicySource & { policyOverride: Partial<PriceMatchPolicy> })
    | undefined,
): PriceMatchPolicySource[] {
  if (source === undefined || (source.scope !== "STORE" && !source.enabled)) {
    return [];
  }

  return [
    {
      scope: source.scope,
      sourceId: source.sourceId,
      enabled: source.enabled,
    },
  ];
}

function withPolicySchemaVersion(policy: PriceMatchPolicy): PriceMatchPolicy {
  return {
    ...policy,
    schemaVersion: policy.schemaVersion ?? PRICE_MATCH_POLICY_SCHEMA_VERSION,
    offerExpiryMinutes: policy.offerExpiryMinutes ?? policy.offerTtlMinutes,
  };
}

function validateBps(field: string, value: number | undefined): string | undefined {
  if (value === undefined) {
    return undefined;
  }

  if (Number.isInteger(value) && value >= 0 && value <= 10_000) {
    return undefined;
  }

  return `${field} must be an integer between 0 and 10000`;
}

function validateMoneyFloor(field: string, value: number | undefined): string | undefined {
  if (value === undefined || (Number.isInteger(value) && value >= 0)) {
    return undefined;
  }

  return `${field} must be a non-negative integer minor-unit amount`;
}

function validatePositiveInteger(field: string, value: number | undefined): string | undefined {
  if (value === undefined || (Number.isInteger(value) && value > 0)) {
    return undefined;
  }

  return `${field} must be a positive integer`;
}

function validateThresholdOrder(policy: Partial<PriceMatchPolicy>): string[] {
  const automatic = policy.automaticApprovalMaxDiscountBps;
  const manual = policy.manualReviewAboveDiscountBps;
  if (automatic === undefined || manual === undefined || automatic <= manual) {
    return [];
  }

  return ["automaticApprovalMaxDiscountBps must not exceed manualReviewAboveDiscountBps"];
}

function normalizeEvaluationInput(
  input: PriceMatchPolicyEvaluationInput,
): PriceMatchPolicyEvaluationInput {
  return {
    ...input,
    policy: withPolicySchemaVersion(input.policy),
  };
}

function evaluateHardRejections(
  input: PriceMatchPolicyEvaluationInput,
): PriceMatchPolicyDecision | undefined {
  const reasons = collectHardRejectionReasons(input);
  if (reasons.length === 0) {
    return undefined;
  }

  return toDecision(input.policy, "REJECT", reasons, reasons);
}

function collectHardRejectionReasons(
  input: PriceMatchPolicyEvaluationInput,
): PriceMatchPolicyReasonCode[] {
  return [
    input.competitorDomainApproved ? undefined : "COMPETITOR_DOMAIN_NOT_APPROVED",
    input.sellerApproved ? undefined : "SELLER_NOT_APPROVED",
    input.competitorCondition === input.policy.allowedCondition
      ? undefined
      : "CONDITION_NOT_ALLOWED",
    shouldRequireStock(input) && !input.competitorInStock ? "COMPETITOR_OUT_OF_STOCK" : undefined,
    isStale(input) ? "STALE_COMPETITOR_PRICE" : undefined,
    input.policy.requirePublicPrice !== false && !input.isPublicPrice
      ? "NON_PUBLIC_PRICE"
      : undefined,
    input.currency === input.competitorCurrency ? undefined : "CURRENCY_MISMATCH",
    requestLimitExceeded(input) ? "REQUEST_LIMIT_EXCEEDED" : undefined,
  ].filter((reason): reason is PriceMatchPolicyReasonCode => reason !== undefined);
}

function shouldRequireStock(input: PriceMatchPolicyEvaluationInput): boolean {
  return input.policy.competitorMustBeInStock || input.policy.stockRequirement === "IN_STOCK";
}

function isStale(input: PriceMatchPolicyEvaluationInput): boolean {
  const ageMinutes = (Date.parse(input.now) - Date.parse(input.observedAt)) / 60_000;
  return ageMinutes > (input.policy.maximumPriceAgeMinutes ?? 60);
}

function requestLimitExceeded(input: PriceMatchPolicyEvaluationInput): boolean {
  const customerLimit = input.policy.requestLimitPerCustomerPerDay;
  const productLimit = input.policy.requestLimitPerProductPerDay;
  return (
    (customerLimit !== undefined && (input.requestCountForCustomerToday ?? 0) >= customerLimit) ||
    (productLimit !== undefined && (input.requestCountForProductToday ?? 0) >= productLimit)
  );
}

function evaluateAlreadyCheaper(
  input: PriceMatchPolicyEvaluationInput,
): PriceMatchPolicyDecision | undefined {
  if (input.currentPriceMinor > input.competitorPriceMinor) {
    return undefined;
  }

  return toDecision(input.policy, "ALREADY_CHEAPER", ["ALREADY_CHEAPER"], ["ALREADY_CHEAPER"]);
}

function evaluateOfferConstraints(
  input: PriceMatchPolicyEvaluationInput,
): PriceMatchPolicyDecision | undefined {
  const offerPriceMinor = input.competitorPriceMinor;
  const reasons = collectOfferConstraintReasons(input, offerPriceMinor);
  if (reasons.length === 0) {
    return undefined;
  }

  return constrainedDecision(input, reasons);
}

function collectOfferConstraintReasons(
  input: PriceMatchPolicyEvaluationInput,
  offerPriceMinor: number,
): PriceMatchPolicyReasonCode[] {
  return [
    discountCapExceeded(input) ? "DISCOUNT_CAP_EXCEEDED" : undefined,
    missingCostForMargin(input) ? "MISSING_UNIT_COST" : undefined,
    marginFloorViolated(input, offerPriceMinor) ? "MARGIN_FLOOR_VIOLATED" : undefined,
  ].filter((reason): reason is PriceMatchPolicyReasonCode => reason !== undefined);
}

function discountCapExceeded(input: PriceMatchPolicyEvaluationInput): boolean {
  const discountMinor = input.currentPriceMinor - input.competitorPriceMinor;
  return discountMinor * 10_000 > input.currentPriceMinor * input.policy.maxDiscountBps;
}

function missingCostForMargin(input: PriceMatchPolicyEvaluationInput): boolean {
  return (
    input.policy.minimumMarginBps !== undefined &&
    input.unitCostMinor === undefined &&
    input.policy.missingCostBehavior !== "DISCOUNT_CAP_ONLY"
  );
}

function marginFloorViolated(
  input: PriceMatchPolicyEvaluationInput,
  offerPriceMinor: number,
): boolean {
  if (input.policy.minimumMarginBps === undefined || input.unitCostMinor === undefined) {
    return false;
  }

  return (
    (offerPriceMinor - input.unitCostMinor) * 10_000 <
    offerPriceMinor * input.policy.minimumMarginBps
  );
}

function constrainedDecision(
  input: PriceMatchPolicyEvaluationInput,
  reasons: PriceMatchPolicyReasonCode[],
): PriceMatchPolicyDecision {
  if (input.policy.allowCounteroffer && canCounterofferForReasons(reasons)) {
    return toDecision(
      input.policy,
      "COUNTEROFFER_ALLOWED",
      publicReasonsForConstraints(reasons),
      reasons,
      {
        counterofferPriceMinor: calculateSafeCounterofferMinor(input),
      },
    );
  }

  if (input.policy.allowManualReview) {
    return toDecision(input.policy, "MANUAL_REVIEW", publicReasonsForConstraints(reasons), reasons);
  }

  return toDecision(input.policy, "REJECT", publicReasonsForConstraints(reasons), reasons);
}

function canCounterofferForReasons(reasons: PriceMatchPolicyReasonCode[]): boolean {
  if (reasons.includes("MISSING_UNIT_COST")) {
    return false;
  }

  return reasons.includes("DISCOUNT_CAP_EXCEEDED") || reasons.includes("MARGIN_FLOOR_VIOLATED");
}

function publicReasonsForConstraints(
  reasons: PriceMatchPolicyReasonCode[],
): PriceMatchPolicyReasonCode[] {
  return reasons.includes("DISCOUNT_CAP_EXCEEDED") ? ["DISCOUNT_CAP_EXCEEDED"] : reasons;
}

function calculateSafeCounterofferMinor(input: PriceMatchPolicyEvaluationInput): number {
  const maxDiscountPrice =
    input.currentPriceMinor -
    Math.floor((input.currentPriceMinor * input.policy.maxDiscountBps) / 10_000);
  const marginPrice = calculateMinimumMarginPrice(input);
  const configuredFloor = input.policy.minimumOfferPriceMinor ?? 0;
  return Math.max(maxDiscountPrice, marginPrice, configuredFloor);
}

function calculateMinimumMarginPrice(input: PriceMatchPolicyEvaluationInput): number {
  if (input.policy.minimumMarginBps === undefined || input.unitCostMinor === undefined) {
    return 0;
  }

  return Math.ceil((input.unitCostMinor * 10_000) / (10_000 - input.policy.minimumMarginBps));
}

function approveAutomaticMatch(input: PriceMatchPolicyEvaluationInput): PriceMatchPolicyDecision {
  return toDecision(input.policy, "AUTO_MATCH", ["AUTO_MATCH_ELIGIBLE"], ["AUTO_MATCH_ELIGIBLE"], {
    offerPriceMinor: input.competitorPriceMinor,
  });
}

function toDecision(
  policy: PriceMatchPolicy,
  outcome: PriceMatchPolicyOutcome,
  publicReasonCodes: PriceMatchPolicyReasonCode[],
  internalReasonCodes: PriceMatchPolicyReasonCode[],
  amounts?: { offerPriceMinor?: number; counterofferPriceMinor?: number },
): PriceMatchPolicyDecision {
  return {
    outcome,
    publicReasonCodes,
    internalReasonCodes,
    missingData: internalReasonCodes.includes("MISSING_UNIT_COST") ? ["unitCostMinor"] : [],
    ...(amounts?.offerPriceMinor === undefined ? {} : { offerPriceMinor: amounts.offerPriceMinor }),
    ...(amounts?.counterofferPriceMinor === undefined
      ? {}
      : { counterofferPriceMinor: amounts.counterofferPriceMinor }),
    policySchemaVersion: policy.schemaVersion ?? PRICE_MATCH_POLICY_SCHEMA_VERSION,
  };
}
